import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Coins, ChevronDown } from "lucide-react";
import { LANGUAGES } from "@/i18n";
import { CURRENCIES, useCurrency } from "@/lib/currency";

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    document.documentElement.dir = current.dir;
    document.documentElement.lang = current.code;
  }, [current]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 px-3 h-9 rounded-full border border-border hover:border-gold-400 text-xs font-medium" data-testid="lang-selector">
        <Globe className="w-3.5 h-3.5" />
        <span className="uppercase">{current.code}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 w-44 bg-background border border-border rounded-xl shadow-xl overflow-hidden z-50" data-testid="lang-menu">
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }} className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted text-left ${l.code === current.code ? "text-gold-500" : ""}`} data-testid={`lang-option-${l.code}`}>
              <span>{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CurrencySelector() {
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} className="hidden md:flex items-center gap-1.5 px-3 h-9 rounded-full border border-border hover:border-gold-400 text-xs font-medium" data-testid="currency-selector">
        <Coins className="w-3.5 h-3.5" />
        <span>{current.code}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 w-52 bg-background border border-border rounded-xl shadow-xl overflow-hidden z-50" data-testid="currency-menu">
          {CURRENCIES.map((c) => (
            <button key={c.code} onClick={() => { setCurrency(c.code); setOpen(false); }} className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted text-left ${c.code === currency ? "text-gold-500" : ""}`} data-testid={`currency-option-${c.code}`}>
              <span className="font-mono w-8 text-muted-foreground">{c.symbol}</span>
              <span className="flex-1">{c.code}</span>
              <span className="text-xs text-muted-foreground">{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
